import { canUseDOM } from 'sletat-common-utils/lib/canUseDOM';
import {
    HotelReviewSourceType,
    RatingGrade,
    SeasonsType,
    MonthIntervalTypes,
    AdaptiveType,
    WindowWidthSize,
    TripType,
    Hits
} from '../models/hotel-reviews';
import { defaultTheme } from 'sletat-ui-components2/dist/theme';

export function getHotelReviewSourceLogo(sourceKind: HotelReviewSourceType | null): string {
    switch (sourceKind) {
        case HotelReviewSourceType.Sletat:
            return 'https://static.sletat.ru/images/logotype/logo_237x70.png';

        case HotelReviewSourceType.TripAdvisor:
            return 'https://static.sletat.ru/images/hotels-review/sources_logos/trip_advisor_logo.png';

        case HotelReviewSourceType.TourPravda:
            return 'https://static.sletat.ru/images/hotels-review/sources_logos/tour_pravda_logo.png';

        case HotelReviewSourceType.TopHotels:
            return 'https://static.sletat.ru/images/hotels-review/sources_logos/top_hotels_logo.png';

        case HotelReviewSourceType.Booking:
            return 'https://static.sletat.ru/images/hotels-review/sources_logos/booking_com_logo.png';

        default:
            return '';
    }
}

const isBadRating = (rating: number): boolean => rating <= RatingGrade.Bad;
const isMediumRating = (rating: number): boolean =>
    rating > RatingGrade.Bad && rating < RatingGrade.Good;
const isGoodRating = (rating: number): boolean => rating >= RatingGrade.Good;

const { theme } = defaultTheme;

export function setColorByRating(rating: number): string {
    if (isGoodRating(rating)) return theme.colorVariables.bgRatingGood;
    else if (isMediumRating(rating) || isBadRating(rating)) return theme.colorVariables.bgRatingNormal;
    else return theme.colorVariables.bgFieldDisabled;
}

export function formatDate(date: string | null | undefined): string {
    return date ? new Date(date).toLocaleString().split(',')[0] : '';
}

export const getCheckedMonths = (checkedSeasons: Array<SeasonsType>): MonthIntervalTypes[] => {
    const seasons: MonthIntervalTypes[] = [];
    if (checkedSeasons.includes(SeasonsType.Winter)) {
        seasons.push(
            {
                from: 1,
                to: 2,
            },
            {
                from: 12,
                to: 12,
            }
        );
    }
    if (checkedSeasons.includes(SeasonsType.Spring)) {
        seasons.push({
            from: 3,
            to: 5,
        });
    }
    if (checkedSeasons.includes(SeasonsType.Summer)) {
        seasons.push({
            from: 6,
            to: 8,
        });
    }
    if (checkedSeasons.includes(SeasonsType.Fall)) {
        seasons.push({
            from: 9,
            to: 11,
        });
    }

    return seasons;
};

export const formatTripAdvisorPhotoUrl = (url: string, previewMode = false ): string => {
    const height = previewMode ? 100 : 800;
    const width = previewMode ? 200 : 800;

    if (url.match(/tripadvisor/)) {
        return `${url.split('?')[0]}?w=${width}&h=${height}&s=1`;
    }

    return url;
};

export const getTripTypeLabel = (type: string | undefined): string => {
    if (!type) return '';

    switch (type) {
        case (TripType.Business): {
            return ' Путешествие по работе';
        }
        case (TripType.Couple): {
            return 'Путешествие с парой';
        }
        case (TripType.Family): {
            return 'Отдых с семьёй';
        }
        case (TripType.Friends): {
            return ' Отдых с друзьями';
        }
        case (TripType.Solo): {
            return ' Отдых в одиночку';
        }
        default: {
            return '';
        }
    }
};

// Функция подсвечивает слова, выбранные в фильтре "Интересное в отзывах"
export const highlightingHitsWords = (
    reviewText: string,
    reviewHits: Hits,
    activeFilterHits: number[]
): string => {
    let highlightedWordsText = reviewText;
    const highlightStyle = `"background-color: #ffca00; border-radius: 4px; padding: 1px 5px; margin: 0 3px;"`;

    activeFilterHits.forEach((hit) => {
        const highlightedWords: string[] = [];

        /*  В переменную highlightedWords собираются слова, которые уже были выделены,
            чтобы если слово попадается в отзыве более раза, оно не оборачивалось повторно в span */

        if (reviewHits && hit && reviewHits[hit]) {
            reviewHits[hit].forEach((el: { start: number; end: number; length: number }) => {
                const word = reviewText.substring(el.start, el.end);
                const wordRegExp = new RegExp(word, 'g');

                if (!highlightedWords.includes(word)) {
                    const tag = `<span style=${highlightStyle}>${word}</span>`;
                    highlightedWordsText = highlightedWordsText.replace(wordRegExp, tag);
                    highlightedWords.push(word);
                }
            });
        }
    });

    return highlightedWordsText;
};

export function getReviewsAdaptiveType(scrollWidth: number = 0): AdaptiveType {
    if (!canUseDOM) {
        return AdaptiveType.IsDesktop;
    }

    const width = document.documentElement.clientWidth + scrollWidth;

    if (width <= WindowWidthSize.W520) {
        return AdaptiveType.IsMobile;
    }

    if (width <= WindowWidthSize.W768) {
        return AdaptiveType.IsTabletS;
    }

    if (width <= WindowWidthSize.W980) {
        return AdaptiveType.IsTabletL;
    }

    return AdaptiveType.IsDesktop;
}

export const roundNumber = (num: number) => Number(num.toFixed(1));
